import { Controller, useForm } from "react-hook-form";

import { Button, Input, Text } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import type { OwnerDetails } from "~/v2/api/rest/account";
import { CountryCombobox, Field } from "~/v2/components/form";
import { useLock, useMutationRequest } from "../../../../api/hooks";
import {
  type AffiliateData,
  createAffiliate,
  type DisclosureData,
  disclosuresPatch,
} from "../../../../api/rest/onboarding";
import { Radio } from "../../entityOnboarding/Radio";
import { Back } from "../back";
import type { StepProps } from "../session";

type Fields = {
  employment_status: string;
  employer: string;
  employer_address: string;
  function: string;
  position: string;
  disclosure: string;
  annual_income: string;
  total_net_worth: string;
  liquid_net_worth: string;
  funding_source: string;
  company_name: string;
  street_address: string;
  unit: string;
  country: string;
  postal_code: string;
  city: string;
  state: string;
  company_symbol: string;
  compliance_email: string;
  company_department: string;
  first_name_family: string;
  last_name_family: string;
  non_solicitation_answer: string;
};

const disclosures: (keyof OwnerDetails)[] = [
  // todo: remove when no longer required by backend
  "is_discretionary",
  "is_control_person",
  "is_politically_exposed",
  "is_affiliated_exchange_or_finra",
  "immediate_family_exposed",
];

const affiliates: Record<string, string> = {
  is_control_person: "CONTROLLED_FIRM",
  is_affiliated_exchange_or_finra: "AFFILIATE_FIRM",
  immediate_family_exposed: "IMMEDIATE_FAMILY_EXPOSED",
};

const Disclosures = ({ session }: StepProps) => {
  const lock = useLock();

  // we need session below, so we spread these here
  const {
    account,
    details,
    prev,
    country: { allCountries, nameToAlpha3 },
  } = session;

  const { mutate: disclosureMutation } = useMutationRequest(
    (payload: Partial<DisclosureData>) => disclosuresPatch(account.id, payload),
  );

  const { mutate: affiliateMutation } = useMutationRequest(
    (payload: AffiliateData) => createAffiliate(account.id, payload),
  );

  const form = useForm<Fields>({
    mode: "all",
    defaultValues: {
      disclosure: "none_checked",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = form;

  const isInternational = details?.country_of_tax_residence !== "USA";
  const isControlPerson = watch("disclosure") === "is_control_person";
  const isAffiliatedExchangeOrFINRA =
    watch("disclosure") === "is_affiliated_exchange_or_finra";
  const isAffiliated = isControlPerson || isAffiliatedExchangeOrFINRA;
  const immediateFamilyExposed =
    watch("disclosure") === "immediate_family_exposed";

  const next = () => {
    // call amplitude event
    AmplitudeProvider.dispatch("onboardingv2_step_two_complete");

    // go to next step
    session.next();
  };

  const sendPatch = async (values: Fields) => {
    if (lock.locked) {
      return;
    }

    lock.lock();

    // wait for disclosure patch
    await new Promise<void>((resolve) =>
      disclosureMutation(
        {
          ...values,
          ...disclosures.reduce<Record<string, boolean>>((p, n) => {
            p[n] = n === values.disclosure;
            return p;
          }, {}),
        },
        {
          onSettled: () => {
            lock.unlock();
            resolve();
          },
        },
      ),
    );

    // go next if no affiliate
    if (!affiliates[values.disclosure]) {
      lock.unlock();
      next();
      return;
    }

    // attempt affiliate patch
    await new Promise<void>((resolve, reject) =>
      affiliateMutation(
        {
          ...values,
          type: affiliates[values.disclosure],
          street_address: [values.street_address],
          country: nameToAlpha3(values.country) || "",
        },
        {
          onSuccess: () => {
            resolve();
            next();
          },
          onError: () => {
            reject();
            prev();
          },
          onSettled: () => lock.unlock(),
        },
      ),
    ).catch(() => lock.unlock());
  };

  return (
    <div className="mb-16">
      <h2 className="mb-8 font-bold text-lg">{session.step.title}</h2>
      <form onSubmit={handleSubmit(sendPatch)}>
        <div className="mt-4 flex flex-col items-start gap-4">
          <Field
            label="Do any of the following apply to you or your family?"
            required={true}
          >
            <Controller
              name="disclosure"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="flex w-full flex-col gap-2">
                  <Radio
                    name="disclosure_none_checked"
                    label="None of the below apply to me or my family"
                    isChecked={field.value === "none_checked"}
                    onChange={() => field.onChange("none_checked")}
                  />
                  <Radio
                    name="disclosure_is_affiliated_exchange_or_finra"
                    label="Affiliated or work with a US registered broker dealer or FINRA."
                    isChecked={
                      field.value === "is_affiliated_exchange_or_finra"
                    }
                    onChange={() =>
                      field.onChange("is_affiliated_exchange_or_finra")
                    }
                  />
                  <Radio
                    name="disclosure_is_control_person"
                    label="Senior executive at or 10% shareholder of a publicly traded company."
                    isChecked={field.value === "is_control_person"}
                    onChange={() => field.onChange("is_control_person")}
                  />
                  <Radio
                    name="disclosure_is_politically_exposed"
                    label="I am a senior political figure."
                    isChecked={field.value === "is_politically_exposed"}
                    onChange={() => field.onChange("is_politically_exposed")}
                  />
                  <Radio
                    name="disclosure_immediate_family_exposed"
                    label="I am a family member or relative of a senior political figure."
                    isChecked={field.value === "immediate_family_exposed"}
                    onChange={() => field.onChange("immediate_family_exposed")}
                  />
                </div>
              )}
            />
          </Field>
          <div className="m-0 w-full">
            {isAffiliated && (
              <div className="mt-8 flex flex-col gap-4">
                <Field
                  label="Company Name"
                  required={isAffiliated}
                  error={!!errors.company_name}
                  errorMsg={"You must provide a name"}
                >
                  <Controller
                    control={control}
                    name="company_name"
                    rules={{ required: isAffiliated }}
                    render={({ field }) => (
                      <Input {...field} placeholder="Alpaca" />
                    )}
                  />
                </Field>
                <Field
                  label="Company Symbol"
                  required={isAffiliated}
                  error={!!errors.company_symbol}
                  errorMsg={"You must provide a symbol"}
                >
                  <div>
                    <Controller
                      control={control}
                      name="company_symbol"
                      rules={{ required: isAffiliated }}
                      render={({ field }) => (
                        <Input {...field} placeholder="LPCA" />
                      )}
                    />
                  </div>
                </Field>
                <div className="w-full">
                  <div className="flex w-full items-end justify-between gap-2">
                    <div className="mt-4 grow">
                      <Field
                        label="Street Address"
                        required={isAffiliated}
                        error={!!errors.street_address}
                        errorMsg="Required"
                      >
                        <Controller
                          control={control}
                          name="street_address"
                          rules={{ required: isAffiliated }}
                          render={({ field }) => (
                            <Input placeholder="1234 Alpaca St" {...field} />
                          )}
                        />
                      </Field>
                    </div>
                    <div className="w-full max-w-[25%]">
                      <Field label="Unit">
                        <Controller
                          control={control}
                          name="unit"
                          render={({ field }) => (
                            <Input placeholder="1234" {...field} />
                          )}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-between">
                  <div className="mr-4">
                    <Field
                      label="City"
                      required={isAffiliated}
                      error={!!errors.city}
                      errorMsg="Required"
                    >
                      <Controller
                        name="city"
                        control={control}
                        rules={{ required: isAffiliated }}
                        render={({ field }) => (
                          <Input placeholder="San Mateo" {...field} />
                        )}
                      />
                    </Field>
                  </div>
                  <div className="mr-4 grow">
                    <Field
                      label={"State"}
                      required={isAffiliated}
                      error={!!errors.state}
                      errorMsg="Required"
                    >
                      <Controller
                        name="state"
                        control={control}
                        rules={{ required: isAffiliated }}
                        render={({ field }) => (
                          <Input maxLength={36} placeholder="CA" {...field} />
                        )}
                      />
                    </Field>
                  </div>
                  <div className="w-full max-w-[30%]">
                    <Field
                      label="Postal Code"
                      required={isAffiliated}
                      error={!!errors.postal_code}
                      errorMsg="Required"
                    >
                      <Controller
                        name="postal_code"
                        control={control}
                        rules={{ required: isAffiliated }}
                        render={({ field }) => (
                          <Input placeholder="94401" {...field} />
                        )}
                      />
                    </Field>
                  </div>
                </div>
                <div>
                  <Field
                    label="Country"
                    required={isAffiliated}
                    error={!!errors.country}
                    errorMsg="Required"
                  >
                    <Controller
                      name="country"
                      control={control}
                      rules={{
                        required: isAffiliated,
                        validate: (value) => {
                          if (!isAffiliated) {
                            return true;
                          }

                          return (
                            allCountries.find(({ name }) => name === value) !==
                            undefined
                          );
                        },
                      }}
                      render={({ field }) => (
                        <CountryCombobox
                          name={field.name}
                          placeholder="United States of America"
                          items={allCountries}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Field>
                </div>
                <Field
                  label="Company Compliance Email"
                  required={isAffiliated}
                  error={!!errors.compliance_email}
                  errorMsg={"You must provide an email"}
                >
                  <Controller
                    control={control}
                    name="compliance_email"
                    rules={{
                      required: isAffiliated,
                      pattern: /\S+@\S+\.\S+/,
                    }}
                    render={({ field }) => (
                      <Input {...field} placeholder="john.doe@example.com" />
                    )}
                  />
                </Field>
                <Field
                  label="Department Name"
                  required={isAffiliated}
                  error={!!errors.company_department}
                  errorMsg={"You must provide a department"}
                >
                  <Controller
                    control={control}
                    name="company_department"
                    rules={{ required: isAffiliated }}
                    render={({ field }) => (
                      <Input {...field} placeholder="Accounting" />
                    )}
                  />
                </Field>
              </div>
            )}

            {immediateFamilyExposed && (
              <div className="mt-8 flex flex-col gap-4">
                <Field
                  label="Relative's First Name"
                  required={immediateFamilyExposed}
                  error={!!errors.first_name_family}
                  errorMsg={"You must provide a first name"}
                >
                  <Controller
                    control={control}
                    name="first_name_family"
                    rules={{ required: immediateFamilyExposed }}
                    render={({ field }) => (
                      <Input {...field} placeholder="John" />
                    )}
                  />
                </Field>
                <Field
                  label="Relative's Last Name"
                  required={immediateFamilyExposed}
                  error={!!errors.last_name_family}
                  errorMsg={"You must provide a last name"}
                >
                  <Controller
                    control={control}
                    name="last_name_family"
                    rules={{ required: immediateFamilyExposed }}
                    render={({ field }) => (
                      <Input {...field} placeholder="Doe" />
                    )}
                  />
                </Field>
              </div>
            )}
          </div>

          {isInternational && (
            <Field
              label="Where did you hear about Alpaca?"
              required={isInternational}
            >
              <Controller
                control={control}
                name="non_solicitation_answer"
                rules={{ required: isInternational }}
                render={({ field }) => (
                  <div className="mt-4 flex w-full flex-col gap-2">
                    <Radio
                      name="non_solicitation_answer_INTERNET_SEARCH"
                      label="Internet search"
                      isChecked={field.value === "INTERNET_SEARCH"}
                      onChange={() => field.onChange("INTERNET_SEARCH")}
                    />
                    <Radio
                      name="non_solicitation_answer_REFERRAL"
                      label="A referral"
                      isChecked={field.value === "REFERRAL"}
                      onChange={() => field.onChange("REFERRAL")}
                    />
                    <Radio
                      name="non_solicitation_answer_SEEKING_INVESTMENT"
                      label=" I was seeking investment services"
                      isChecked={field.value === "SEEKING_INVESTMENT"}
                      onChange={() => field.onChange("SEEKING_INVESTMENT")}
                    />
                    <Radio
                      name="non_solicitation_answer_OTHER"
                      label="Other"
                      isChecked={field.value === "OTHER"}
                      onChange={() => field.onChange("OTHER")}
                    />
                  </div>
                )}
              />
            </Field>
          )}
        </div>
        <Text className="my-8 text-gray-600">
          By clicking the button below, I confirm that AlpacaDB, Inc. and its
          registered broker-dealer subsidiary Alpaca Securities LLC (together
          &ldquo;Alpaca&ldquo;) did not directly solicit or market to me in
          opening a brokerage account.
        </Text>
        <div className="flex items-center justify-end gap-2">
          <Back patch={true} session={session} />
          <Button type="submit" disabled={isSubmitting || !isValid}>
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Disclosures;
